import { useState } from 'react';

import CryptoJS from 'crypto-js';

import supabase from "../utils/Supabase";
import { generateSafeFilename } from "../utils/Storage"
import { getPlatScripts } from "../utils/GetPlatScripts";
import { sortScripts } from '../utils/SortScripts';
import { checkMD5HashExists } from '../utils/Database';
import { refreshIndexingRecords } from '../utils/Indexing';

import useScriptsStore from "../stores/PlatScripts";
import useIndexingStore from "../stores/Indexing";

import { LoadingText } from './LoadingText';

const FileUploadButton: React.FC<{bucket?: string}> = ({bucket = "files"}) => {
  const [uploading, setUploading] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [currentFileNumber, setCurrentFileNumber] = useState(1);
  const [currentFileName, setCurrentFileName] = useState("");

  const setFilesWithoutIndex = useIndexingStore((state) => state.setFilesWithoutIndex);

  const setTotalScripts = useScriptsStore((state) => state.setTotalScripts);
  const setTotalScriptsCount = useScriptsStore((state) => state.setTotalCount);
  const sortConfig = useScriptsStore((state) => state.sortConfig);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    let successCount = 0;
    let failureCount = 0;
    let skippedCount = 0;

    setUploading(true);
    setTotalFiles(files.length);
    setCurrentFileNumber(1);

    const uploadResults: string[] = [];

    for (const file of Array.from(files)) {
      const fileType = file.type || "application/octet-stream";
      const rawName = file.name;
      setCurrentFileName(rawName);
      const safeName = generateSafeFilename(rawName);
      const fileWithSafeName = new File([file], safeName, { type: fileType });

      const arrayBuffer = await file.arrayBuffer();
      const wordArray = CryptoJS.lib.WordArray.create(new Uint8Array(arrayBuffer) as any);
      const md5Hash = CryptoJS.MD5(wordArray).toString(CryptoJS.enc.Hex);
      const hashExists = await checkMD5HashExists(md5Hash);

      if (hashExists.databaseResults.length > 0 || hashExists.indexingResults.length > 0) {
        if (hashExists.databaseResults.length > 0) {
          uploadResults.unshift(`⚠️ ${file.name} already exists as ${hashExists.databaseResults[0]["Title"]}.${hashExists.databaseResults[0].record_deleted === true ? ` Additionally, the database record was deleted for the following reason: ❌ ${hashExists.databaseResults[0].flag_reason}` : ""}.`);
        } else {
          uploadResults.unshift(`⚠️ ${file.name} already exists as ${hashExists.indexingResults[0].file_name} with the indexing status ${hashExists.indexingResults[0].index_status}.`);
        }
        setMessages([...uploadResults]);
        setCurrentFileNumber(prev => prev + 1);
        skippedCount++;
        continue;
      }

      const { data: fileData, error: fileError } = await supabase.storage
        .from(bucket)
        .upload(safeName, fileWithSafeName);

      if (!fileData || fileError) {
        uploadResults.unshift(`❌ ${file.name} failed to upload as ${safeName}: ${fileError?.message}`);
        failureCount++;
      } else {
        if (bucket === 'files') {
          const {data: indexData, error: indexError} = await supabase
            .from('indexing')
            .insert({
              file_name: fileData.path,
              file_id: fileData.id,
              file_type: fileType,
              file_hash: md5Hash || '',
            })
            .select("*");

          if (indexError || !indexData || indexData.length === 0) {
            uploadResults.unshift(`❌ ${file.name} uploaded as ${safeName} but failed to add for indexing: ${indexError?.message || "Unknown error"}`);
            failureCount++;
          }

          if (indexData && indexData.length > 0) {
            uploadResults.unshift(`✅ ${file.name} uploaded successfully as ${safeName} and added for indexing.`);
            successCount++;
          }
        } else {
          uploadResults.unshift(`✅ ${file.name} uploaded successfully as ${safeName}.`);
          successCount++;
        }
      }

      setMessages([...uploadResults]);
      setCurrentFileNumber(prev => prev + 1);
    }

    uploadResults.unshift(`Upload complete: ✅ ${successCount} succeeded, ❌ ${failureCount} failed, ⚠️ ${skippedCount} skipped.`);

    setMessages([...uploadResults]);
    setUploading(false);

    if (bucket === "files" && successCount > 0) {
      // Refresh the list of files without index after upload
      const updatedIndexing = await refreshIndexingRecords();
      setFilesWithoutIndex(updatedIndexing);
    }

    if (bucket === "platinum-files" && successCount > 0) {
      // Refresh the list of Platinum scripts after upload
      const updatedScripts = await getPlatScripts();
      if (updatedScripts) {
        const sortedScripts = sortScripts(updatedScripts, sortConfig);
        setTotalScripts(sortedScripts as []);
        setTotalScriptsCount(sortedScripts.length);
      }
    }
  };

  return (
    <div className="mb-5">
      {!uploading ?
        (
          <input
            type="file"
            multiple
            onClick={() => setMessages([])}
            onChange={handleFileUpload}
            disabled={uploading}
            accept=".pdf,.doc,.docx,.txt"
          />
        ) : (
          <p className="py-4 m-0 fw-bold">
            <LoadingText text={`Uploading file ${currentFileNumber} of ${totalFiles}: ${currentFileName}`} className="mb-0" />
          </p>
        )
      }
      {messages.map((msg, idx) => (
        <p key={idx} className="mb-2">{msg}</p>
      ))}
    </div>
  );
};

export default FileUploadButton;
